import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUsers } from '../UserContext';
import { collection, getDocs, addDoc, query, where, serverTimestamp, doc, updateDoc, writeBatch } from "firebase/firestore";
import EditableText from "./EditableText";
import { db } from "../firebase";
import genericProfilePic from "../genericprofilepic.png";
import { notifyUser, notifyAllUsers } from "../utils/notificationHelpers";

export default function Wall() {
  const [user, setUser] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { username } = useParams();
  const navigate = useNavigate();
  const { users, loading } = useUsers();
  const currentUserId = localStorage.getItem("user_id");

  useEffect(() => {
    if (loading) return;

    // /wall (no name) shows your own wall
    const foundUser = username
      ? users.find(u => `${u.first_name}${u.last_name}` === username)
      : users.find(u => u.id === currentUserId);

    setUser(foundUser || null);
    setNotFound(!foundUser);
    if (foundUser) {
      fetchNotes(foundUser.id);
      fetchComments(foundUser.id);
    }
  }, [username, users, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotes = async (wallUserId) => {
    const snapshot = await getDocs(
      query(collection(db, 'wallNotes'), where('wall_user_id', '==', wallUserId))
    );
    const notesList = snapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        created_at: data.created_at?.toDate() || new Date(),
      };
    });
    setNotes(notesList.sort((a, b) => b.created_at - a.created_at));
  };

  const fetchComments = async (wallUserId) => {
    const snapshot = await getDocs(
      query(collection(db, 'wallNoteComments'), where('wall_user_id', '==', wallUserId))
    );
    const grouped = {};
    snapshot.docs.forEach(d => {
      const data = d.data();
      const comment = { id: d.id, ...data, created_at: data.created_at?.toDate() || new Date() };
      if (!grouped[data.note_id]) grouped[data.note_id] = [];
      grouped[data.note_id].push(comment);
    });
    Object.keys(grouped).forEach(noteId => {
      grouped[noteId].sort((a, b) => a.created_at - b.created_at);
    });
    setComments(grouped);
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();

    if (!noteTitle.trim() || !noteContent.trim()) {
      alert("Please fill in both title and content");
      return;
    }

    setIsSubmitting(true);

    const newNote = {
      wall_user_id: user.id,
      author_user_id: currentUserId,
      title: noteTitle,
      content: noteContent,
      created_at: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, 'wallNotes'), newNote);
      const optimisticNote = { id: docRef.id, ...newNote, created_at: new Date() };
      setNotes(prev => [optimisticNote, ...prev]);
      setNoteTitle("");
      setNoteContent("");

      const currentUser = users.find(u => u.id === currentUserId);
      const fromName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : 'Someone';
      const wallOwner = `${user.first_name} ${user.last_name}`.trim();
      const snippet = noteContent.length > 60 ? noteContent.slice(0, 60) + '…' : noteContent;
      notifyAllUsers({
        fromUserId: currentUserId,
        fromName,
        type: 'wall_note',
        message: `${fromName} posted a note on ${wallOwner}'s wall: "${snippet}"`,
        url: `/user/${user.first_name}${user.last_name}`,
      }).catch(() => {});
    } catch (err) {
      console.log("Error posting note:", err);
      alert("Failed to post note. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitComment = async (e, noteId) => {
    e.preventDefault();
    const content = (commentInputs[noteId] || "").trim();
    if (!content) return;

    setSubmittingComment(noteId);

    const newComment = {
      note_id: noteId,
      wall_user_id: user.id,
      author_user_id: currentUserId,
      content,
      created_at: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, 'wallNoteComments'), newComment);
      const optimistic = { id: docRef.id, ...newComment, created_at: new Date() };
      setComments(prev => ({
        ...prev,
        [noteId]: [...(prev[noteId] || []), optimistic],
      }));
      setCommentInputs(prev => ({ ...prev, [noteId]: '' }));

      // Notify the wall owner that someone commented (skip if commenting on own wall)
      const currentUser = users.find(u => u.id === currentUserId);
      const fromName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : 'Someone';
      const note = notes.find(n => n.id === noteId);
      const snippet = content.length > 60 ? content.slice(0, 60) + '…' : content;
      await notifyUser({
        toUserId: user.id,
        fromUserId: currentUserId,
        fromName,
        type: 'wall_comment',
        message: `${fromName} commented on your wall note "${note?.title || 'a note'}": "${snippet}"`,
        url: `/user/${user.first_name}${user.last_name}`,
      });
    } catch (err) {
      console.error("Error posting comment:", err);
    } finally {
      setSubmittingComment(null);
    }
  };

  const editNote = async (noteId, { title, text }) => {
    await updateDoc(doc(db, 'wallNotes', noteId), { title, content: text, edited_at: serverTimestamp() });
    setNotes(prev => prev.map(n => n.id === noteId ? { ...n, title, content: text, edited_at: new Date() } : n));
  };

  // Removes the note and its comments together
  const deleteNote = async (noteId) => {
    const batch = writeBatch(db);
    (comments[noteId] || []).forEach(c => batch.delete(doc(db, 'wallNoteComments', c.id)));
    batch.delete(doc(db, 'wallNotes', noteId));
    await batch.commit();
    setNotes(prev => prev.filter(n => n.id !== noteId));
    setComments(prev => { const next = { ...prev }; delete next[noteId]; return next; });
  };

  const editComment = async (noteId, commentId, text) => {
    await updateDoc(doc(db, 'wallNoteComments', commentId), { content: text, edited_at: serverTimestamp() });
    setComments(prev => ({
      ...prev,
      [noteId]: (prev[noteId] || []).map(c => c.id === commentId ? { ...c, content: text, edited_at: new Date() } : c),
    }));
  };

  const deleteComment = async (noteId, commentId) => {
    const batch = writeBatch(db);
    batch.delete(doc(db, 'wallNoteComments', commentId));
    await batch.commit();
    setComments(prev => ({ ...prev, [noteId]: (prev[noteId] || []).filter(c => c.id !== commentId) }));
  };

  if (!loading && notFound) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h3>We couldn't find that wall.</h3>
        <p className="mediumtext">The person may have changed their name.</p>
        <Link to="/users">
          <button className="brownroundbutton" style={{ cursor: 'pointer' }}>Back to the Community</button>
        </Link>
      </div>
    );
  }

  if (loading || !user) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h3>Loading...</h3>
      </div>
    );
  }

  const isOwnWall = currentUserId === user.id;

  return (
    <div id="wall">
      <br />
      <br />
      <h3 id="wallname">
        {user.first_name} {user.last_name}'s Wall
      </h3>
      <br />

      {user.profile_pic_url && (
        <img
          id="profilepic"
          src={user.profile_pic_url}
          alt={`${user.first_name} ${user.last_name}'s profile`}
        />
      )}

      <br />
      <br />

      {/* Post a Note Form — visible to all logged-in users */}
      {currentUserId && (
        <div className="roundedbox">
          <h4 className="data-header">{isOwnWall ? 'Post a Note' : `Post on ${user.first_name}'s Wall`}</h4>
          <form onSubmit={handleSubmitNote}>
            <div style={{ marginBottom: '15px' }}>
              <input
                id="noteTitle"
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="Enter note title..."
                style={{ width: '80%', padding: '10px', fontSize: '16px', borderRadius: '5px', border: '1px solid #ccc' }}
                disabled={isSubmitting}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <textarea
                id="noteContent"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Write your note here..."
                rows="6"
                style={{ width: '80%', padding: '10px', fontSize: '16px', borderRadius: '5px', border: '1px solid #ccc', resize: 'vertical' }}
                disabled={isSubmitting}
              />
            </div>
            <button type="submit" className="btn submit-pic" disabled={isSubmitting}>
              {isSubmitting ? 'Posting...' : 'Post Note'}
            </button>
          </form>
        </div>
      )}

      <br />
      <br />

      {/* Display Notes */}
      <div>
        <h4 className="data-header">Wall Notes ({notes.length})</h4>

        {notes.length === 0 ? (
          <div className="roundedbox2">
            <p className="commentary-text">No notes on {user.first_name} {user.last_name}'s wall yet.</p>
          </div>
        ) : (
          notes.map((note) => {
            const author = users.find(u => u.id === note.author_user_id);
            const noteComments = comments[note.id] || [];

            return (
              <div className="roundedbox2" key={note.id}>
                <div style={{ marginBottom: '10px' }}>
                  {author && author.profile_pic_url && (
                    <img
                      id="smallprofilepic"
                      src={author.profile_pic_url}
                      alt={`${author.first_name} ${author.last_name}'s profile`}
                    />
                  )}
                  <p className="commentary-text">
                    <b>{author ? `${author.first_name} ${author.last_name}` : 'Unknown User'} posted:</b>
                  </p>
                </div>

                <EditableText
                  title={note.title || ''}
                  renderTitle={(t) => <h5 style={{ marginBottom: '10px', fontSize: '18px' }}>{t}</h5>}
                  text={note.content}
                  edited={!!note.edited_at}
                  itemLabel="note"
                  canEdit={note.author_user_id === currentUserId}
                  canDelete={note.author_user_id === currentUserId || isOwnWall}
                  onSave={(vals) => editNote(note.id, vals)}
                  onDelete={() => deleteNote(note.id)}
                />

                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                  Posted on {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                </p>

                {/* Comments */}
                {noteComments.length > 0 && (
                  <div style={{ marginTop: '16px', borderTop: '1px solid #c8d8f0', paddingTop: '12px' }}>
                    {noteComments.map(comment => {
                      const commentAuthor = users.find(u => u.id === comment.author_user_id);
                      return (
                        <div key={comment.id} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                          {commentAuthor && commentAuthor.profile_pic_url && (
                            <img
                              id="smallprofilepic"
                              src={commentAuthor.profile_pic_url}
                              alt={`${commentAuthor.first_name}'s profile`}
                            />
                          )}
                          <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: '10px', padding: '8px 12px', textAlign: 'left', flex: 1 }}>
                            <p style={{ margin: 0, fontFamily: 'Hebrew', fontSize: '1.5rem', fontWeight: 'bold', color: '#0D3B8C' }}>
                              {commentAuthor ? `${commentAuthor.first_name} ${commentAuthor.last_name}` : 'Unknown'}
                            </p>
                            <EditableText
                              text={comment.content}
                              edited={!!comment.edited_at}
                              itemLabel="comment"
                              compact
                              textClassName=""
                              textStyle={{ margin: '4px 0 0', fontFamily: 'Hebrew', fontSize: '1.5rem', color: '#333' }}
                              canEdit={comment.author_user_id === currentUserId}
                              canDelete={comment.author_user_id === currentUserId || isOwnWall || note.author_user_id === currentUserId}
                              onSave={({ text }) => editComment(note.id, comment.id, text)}
                              onDelete={() => deleteComment(note.id, comment.id)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Comment form — only for logged-in users */}
                {currentUserId && (
                  <form onSubmit={(e) => handleSubmitComment(e, note.id)} style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={commentInputs[note.id] || ''}
                      onChange={(e) => setCommentInputs(prev => ({ ...prev, [note.id]: e.target.value }))}
                      placeholder="Write a comment..."
                      style={{ flex: 1, padding: '8px 12px', fontSize: '14px', borderRadius: '20px', border: '1px solid #c8d8f0' }}
                      disabled={submittingComment === note.id}
                    />
                    <button
                      type="submit"
                      className="respond-btn"
                      style={{ marginTop: 0, padding: '8px 18px', cursor: 'pointer' }}
                      disabled={submittingComment === note.id}
                    >
                      {submittingComment === note.id ? '...' : 'Post'}
                    </button>
                  </form>
                )}
              </div>
            );
          })
        )}
      </div>

      <br />
      <div style={{ textAlign: 'center' }}>
        <Link to="/users">
          <button className="brownroundbutton" style={{ cursor: 'pointer' }}>
            Back to the Community
          </button>
        </Link>
      </div>
    </div>
  );
}
